import { useCallback, useState, useRef } from 'react';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import {
  DEFAULT_TABLE_STATE,
  TableState,
} from 'src/components/DataTable/types/DataTable';

export interface UseTableStateReturn<TFilters> {
  tableState: TableState<TFilters>;
  initialState: Partial<TableState<TFilters>>;
  setFilters: (filters: Partial<TFilters>) => void;
  setPage: (page: number) => void;
  resetState: () => void;
}

export function useTableState<TFilters>(
  initialState?: Partial<TableState<TFilters>>
): UseTableStateReturn<TFilters> {
  const initialStateRef = useRef(initialState);

  const [tableState, setTableState] = useState({
    ...DEFAULT_TABLE_STATE,
    ...initialState,
  } as TableState<TFilters>);

  const setFilters = useCallback((filters: Partial<TFilters>): void => {
    setTableState((prevState) => {
      const nextState = { ...prevState, ...filters, page: 1 };
      return omitBy(nextState, isUndefined) as TableState<TFilters>;
    });
  }, []);

  const setPage = useCallback((page: number): void => {
    setTableState((prevState) => ({
      ...prevState,
      page,
    }));
  }, []);

  const resetState = useCallback((): void => {
    setTableState({
      ...DEFAULT_TABLE_STATE,
      ...initialStateRef.current,
    } as TableState<TFilters>);
  }, []);

  return {
    tableState,
    initialState: initialStateRef.current,
    setFilters,
    setPage,
    resetState,
  };
}
